import { CityAction, CityProductionActionTypes } from '../actions/city.actions';
import { Review, CityForm } from 'src/app/interfaces/data';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromRoot from './index';

export interface CityReviews {
  dataReview: Review;
  steps: { id: string; save: boolean }[];
  transactionId: string;
  hash: string;
  test: boolean;
  cityForm: CityForm;
  currentStep: { id: string; save: boolean };
  currentStepId: number;
  nextStep: { id: string; save: boolean };
  error: boolean;
  loading: boolean;
}

export const initialState: CityReviews = {
  steps: [
    { id: 'welcome', save: false },
    { id: 'landscapes', save: false },
    { id: 'travelers', save: false },
    { id: 'experiences', save: false },
    { id: 'step-days', save: false },
    { id: 'motivations', save: true },
    { id: 'optionals', save: true },
    { id: 'finish', save: true }
  ],
  dataReview: null,
  transactionId: null,
  hash: null,
  test: true,
  cityForm: null,
  currentStep: { id: 'welcome', save: false },
  currentStepId: 0,
  nextStep: null,
  error: null,
  loading: null
};

export function cityReducer(state = initialState, action: CityAction): CityReviews {
  switch (action.type) {
    case CityProductionActionTypes.LoadDataReview: {
      return {
        ...state,
        transactionId: action.payload.transactionId,
        hash: action.payload.hash,
        test: action.payload.test,
        loading: true
      };
    }
    case CityProductionActionTypes.LoadDataReviewSuccessful: {
      return {
        ...state,
        error: false,
        currentStepId: 0,
        cityForm: action.payload.cityForm,
        loading: false,
        dataReview: {
          ...state.dataReview,
          consent: false
        }
      };
    }

    case CityProductionActionTypes.LoadDataReviewError: {
      return {
        ...state,
        error: true,
        loading: false
      };
    }

    case CityProductionActionTypes.NextStep: {
      return {
        ...state
      };
    }
    case CityProductionActionTypes.PreviousStep: {
      return {
        ...state
      };
    }
    case CityProductionActionTypes.SetStep: {
      return {
        ...state,
        currentStep: action.payload
      };
    }
    case CityProductionActionTypes.ChangeNavigation: {
      return state;
    }

    case CityProductionActionTypes.UpdateLandscapes: {
      return {
        ...state,
        dataReview: {
          ...state.dataReview,
          landscapes: action.payload
        }
      };
    }

    case CityProductionActionTypes.UpdateExperiences: {
      return {
        ...state,
        dataReview: {
          ...state.dataReview,
          experiences: action.payload
        }
      };
    }

    case CityProductionActionTypes.UpdateMotivations: {
      return {
        ...state,
        dataReview: {
          ...state.dataReview,
          motivations: action.payload
        }
      };
    }

    case CityProductionActionTypes.UpdateTravelers: {
      return {
        ...state,
        dataReview: {
          ...state.dataReview,
          travelers: action.payload
        }
      };
    }

    case CityProductionActionTypes.UpdateDayRange: {
      return {
        ...state,
        dataReview: {
          ...state.dataReview,
          dayRange: action.payload
        }
      };
    }

    case CityProductionActionTypes.UpdateAnsweredQuestions: {
      return {
        ...state,
        dataReview: {
          ...state.dataReview,
          answeredQuestions: action.payload
        }
      };
    }

    case CityProductionActionTypes.UpdateFeedback: {
      return {
        ...state,
        dataReview: {
          ...state.dataReview,
          feedback: action.payload
        }
      };
    }

    case CityProductionActionTypes.UpdateConsent: {
      return {
        ...state,
        dataReview: {
          ...state.dataReview,
          consent: action.payload
        }
      };
    }
    case CityProductionActionTypes.SaveReviewSuccess: {
      return {
        ...state,
        error: false,
        dataReview: {
          ...state.dataReview,
          id: action.payload
        }
      };
    }

    case CityProductionActionTypes.SaveReviewError: {
      return {
        ...state,
        error: true
      };
    }

    default: {
      return state;
    }
  }
}

export interface State extends fromRoot.State {
  cityModule: CityReviews;
}

const getCityFeatureState = createFeatureSelector<CityReviews>('cityModule');

export const getDataReview = createSelector(getCityFeatureState, state => state.dataReview);

export const getCityForm = createSelector(getCityFeatureState, state => state.cityForm);

export const getSteps = createSelector(getCityFeatureState, state => state.steps);

export const getCurrentStep = createSelector(getCityFeatureState, state => state.currentStep);

export const getCurrentStepId = createSelector(getCityFeatureState, state => {
  const indexStep = state.steps.findIndex(item => item.id === state.currentStep.id);
  return indexStep === -1 ? 0 : indexStep;
});

export const getSaveStep = createSelector(getCityFeatureState, state => state.dataReview);

export const getNextStep = createSelector(
  getCurrentStepId,
  getSteps,
  (currentStepId, steps) => steps[currentStepId + 1]
);

export const getPreviousStep = createSelector(
  getCurrentStepId,
  getSteps,
  (currentStepId, steps) => steps[currentStepId - 1]
);

export const getAnsweredQuestions = createSelector(getCityFeatureState, state =>
  state.dataReview ? state.dataReview.answeredQuestions : null
);

export const getConsent = createSelector(getCityFeatureState, state =>
  state.dataReview ? state.dataReview.consent : null
);

export const getError = createSelector(getCityFeatureState, state => state.error);

export const getLoading = createSelector(getCityFeatureState, state => state.loading);
